/**
 * Cisco Switch Database
 *
 * Cisco switches for AI/ML GPU clusters including NCP-compliant models.
 *
 * Data sources: research/switch-specs.md
 */

import type { SwitchModel } from '../../types/switches';

/**
 * Cisco N9100 Series (NCP-Compliant, Spectrum-4 based)
 */
export const CISCO_N9100: SwitchModel = {
  id: 'cisco-n9100',
  name: 'N9100',
  vendor: 'Cisco',
  productName: 'Cisco N9100 Series',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['64x800G', '128x400G', '256x200G', '256x100G'],
  },
  power: {
    typicalWatts: 1000, // Estimate - similar to Spectrum-4
    maxWatts: 1600,
    idleWatts: 420,
    perPortWatts: 12,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 438,
    depthMm: 600,
    weightKg: 22,
    airflow: 'front-to-back',
    maxBtuPerHour: 5460,
  },
  performance: {
    switchingCapacityTbps: 51.2,
    forwardingRateMpps: 33300,
    latencyNs: 300,
    bufferSizeMB: 160,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'NVIDIA Spectrum-4 ASIC',
      'NCP (NVIDIA Cloud Partner) certified',
      'NCP reference architecture compliant',
    ],
  },
  metadata: {
    productUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/nexus-9000-series-switches/nexus-9000-series-switches-ai-clusters-wp.html',
    releaseYear: 2024,
    endOfLife: false,
    notes: 'NCP-certified switch powered by NVIDIA Spectrum-4 ASIC for AI workloads.',
  },
};

/**
 * Cisco Nexus 9364E-SG2 (Silicon One G200)
 */
export const CISCO_NEXUS_9364E_SG2: SwitchModel = {
  id: 'cisco-nexus-9364e-sg2',
  name: 'Nexus 9364E-SG2',
  vendor: 'Cisco',
  productName: 'Cisco Nexus 9364E-SG2',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP', // Supports both QSFP-DD and OSFP
    supportsBreakout: true,
    breakoutOptions: ['64x800G', '128x400G', '256x200G', '512x100G'],
  },
  power: {
    typicalWatts: 1100,
    maxWatts: 1800,
    idleWatts: 450,
    perPortWatts: 13,
    psuConfig: {
      count: 2,
      capacity: 2200,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 438,
    depthMm: 610,
    weightKg: 24,
    airflow: 'front-to-back',
    maxBtuPerHour: 6140,
  },
  performance: {
    switchingCapacityTbps: 51.2,
    forwardingRateMpps: 38000,
    latencyNs: 280,
    bufferSizeMB: 192,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'Cisco Silicon One G200 ASIC',
      'Validated with NVIDIA H100/H200 DGX',
      'RoCEv2 optimized',
      'Scales to 64K GPUs',
      'Multitenancy support',
    ],
  },
  metadata: {
    productUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/nexus-9000-series-switches/nexus-9000-series-switches-ai-clusters-wp.html',
    releaseYear: 2024,
    endOfLife: false,
    notes: 'High-radix 800G switch with Cisco Silicon One G200. Supports 1K-32K GPU deployments.',
  },
};

/**
 * All Cisco switches
 */
export const CISCO_SWITCHES: readonly SwitchModel[] = [
  CISCO_N9100,
  CISCO_NEXUS_9364E_SG2,
] as const;

/**
 * Get Cisco switch by ID
 */
export function getCiscoSwitch(id: string): SwitchModel | undefined {
  return CISCO_SWITCHES.find((sw) => sw.id === id);
}
